import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },
  register: async (data) => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// Users API
export const usersApi = {
  getAll: async () => {
    const response = await api.get('/users');
    return response.data;
  },
  getManagers: async () => {
    const response = await api.get('/users/managers');
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },
  update: async (userId, data) => {
    const response = await api.put(`/users/${userId}`, data);
    return response.data;
  },
  delete: async (userId) => {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
  },
};

// Departments API
export const departmentsApi = {
  getAll: async () => {
    const response = await api.get('/departments');
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/departments', data);
    return response.data;
  },
  update: async (deptId, data) => {
    const response = await api.put(`/departments/${deptId}`, data);
    return response.data;
  },
  delete: async (deptId) => {
    const response = await api.delete(`/departments/${deptId}`);
    return response.data;
  },
  getManagersByDeptName: async (deptName) => {
    const response = await api.get(`/departments/by-name/${encodeURIComponent(deptName)}/managers`);
    return response.data;
  },
  getUserDepartmentAccess: async () => {
    const response = await api.get('/departments/user-access');
    return response.data;
  },
  getDepartmentVouchers: async () => {
    const response = await api.get('/vouchers/department');
    return response.data;
  },
};

// Finance Heads API
export const financeHeadsApi = {
  getAll: async () => {
    const response = await api.get('/finance-heads');
    return response.data;
  },
};

// Vouchers API
export const vouchersApi = {
  getAll: async (status) => {
    const params = status ? { status, limit: 10000 } : { limit: 10000 };
    const response = await api.get('/vouchers', { params });
    return response.data;
  },
  getAllForAudit: async () => {
    const response = await api.get('/vouchers/all', { params: { limit: 10000 } });
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/vouchers/${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/vouchers', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/vouchers/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/vouchers/${id}`);
    return response.data;
  },
  duplicate: async (id) => {
    const response = await api.post(`/vouchers/${id}/duplicate`);
    return response.data;
  },
  submit: async (id) => {
    const response = await api.post(`/vouchers/${id}/submit`);
    return response.data;
  },
  managerApprove: async (id, note) => {
    const response = await api.post(`/vouchers/${id}/manager-approve`, { note });
    return response.data;
  },
  managerReject: async (id, note) => {
    const response = await api.post(`/vouchers/${id}/manager-reject`, { note });
    return response.data;
  },
  additionalManagerApprove: async (id, note) => {
    const response = await api.post(`/vouchers/${id}/additional-manager-approve`, { note });
    return response.data;
  },
  additionalManagerReject: async (id, note) => {
    const response = await api.post(`/vouchers/${id}/additional-manager-reject`, { note });
    return response.data;
  },
  holdVoucher: async (id, note) => {
    const response = await api.post(`/vouchers/${id}/hold`, { note });
    return response.data;
  },
  releaseHold: async (id, note) => {
    const response = await api.post(`/vouchers/${id}/release-hold`, { note });
    return response.data;
  },
  internalAuditApprove: async (id, note) => {
    const response = await api.post(`/vouchers/${id}/internal-audit-approve`, { note });
    return response.data;
  },
  internalAuditReject: async (id, note) => {
    const response = await api.post(`/vouchers/${id}/internal-audit-reject`, { note });
    return response.data;
  },
  financeHeadApprove: async (id, note) => {
    const response = await api.post(`/vouchers/${id}/finance-head-approve`, { note });
    return response.data;
  },
  financeHeadReject: async (id, note) => {
    const response = await api.post(`/vouchers/${id}/finance-head-reject`, { note });
    return response.data;
  },
  accountsApprove: async (id, note) => {
    const response = await api.post(`/vouchers/${id}/accounts-approve`, { note });
    return response.data;
  },
  accountsReject: async (id, note) => {
    const response = await api.post(`/vouchers/${id}/accounts-reject`, { note });
    return response.data;
  },
  markPaid: async (id, paymentReference, note) => {
    const response = await api.post(`/vouchers/${id}/mark-paid`, { paymentReference, note });
    return response.data;
  },
  getAuditLogs: async (id) => {
    const response = await api.get(`/vouchers/${id}/audit-logs`);
    return response.data;
  },
  uploadAttachment: async (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/vouchers/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  uploadPaymentAttachment: async (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/vouchers/${id}/payment-attachment`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  deleteAttachment: async (id, filename) => {
    const response = await api.delete(`/vouchers/${id}/attachments/${filename}`);
    return response.data;
  },
  uploadAdditionalAttachment: async (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/vouchers/${id}/additional-attachment`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  deleteAdditionalAttachment: async (id, filename) => {
    const response = await api.delete(`/vouchers/${id}/additional-attachments/${filename}`);
    return response.data;
  },
};

// Stats API
export const statsApi = {
  get: async () => {
    const response = await api.get('/stats');
    return response.data;
  },
};

// System Audit Logs API (Admin only)
export const systemAuditLogsApi = {
  getAll: async (params = {}) => {
    const response = await api.get('/system-audit-logs', { params });
    return response.data;
  },
  getCount: async (params = {}) => {
    const response = await api.get('/system-audit-logs/count', { params });
    return response.data;
  },
  getActions: async () => {
    const response = await api.get('/system-audit-logs/actions');
    return response.data;
  },
  getModules: async () => {
    const response = await api.get('/system-audit-logs/modules');
    return response.data;
  },
  getUsers: async () => {
    const response = await api.get('/system-audit-logs/users');
    return response.data;
  },
  export: async (params = {}) => {
    const response = await api.get('/system-audit-logs/export', { 
      params,
      responseType: 'blob'
    });
    return response.data;
  },
};

// System Storage Stats API (Admin only)
export const systemApi = {
  getStorageStats: async () => {
    const response = await api.get('/system/storage-stats');
    return response.data;
  },
};

// Seed API
export const seedApi = {
  seed: async () => {
    const response = await api.post('/seed');
    return response.data;
  },
};

export default api;
